/**
 * LayoutHintsContext — cross-component layout signals.
 *
 * Lets deeply-nested components (e.g. ConversationViewer inside <Outlet />)
 * signal layout changes to GlobalLayout without prop drilling.
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface LayoutHints {
  /** True when a detail panel (e.g. InterlocutorPanel) is open */
  isDetailPanelOpen: boolean;
  /** Signal detail panel open/close from any descendant */
  setDetailPanelOpen: (open: boolean) => void;
}

const LayoutHintsContext = createContext<LayoutHints>({
  isDetailPanelOpen: false,
  setDetailPanelOpen: () => {},
});

export function LayoutHintsProvider({ children }: { children: ReactNode }) {
  const [isDetailPanelOpen, setDetailPanelOpen] = useState(false);

  const stableSetDetailPanelOpen = useCallback((open: boolean) => {
    setDetailPanelOpen(open);
  }, []);

  return (
    <LayoutHintsContext.Provider
      value={{ isDetailPanelOpen, setDetailPanelOpen: stableSetDetailPanelOpen }}
    >
      {children}
    </LayoutHintsContext.Provider>
  );
}

export function useLayoutHints(): LayoutHints {
  return useContext(LayoutHintsContext);
}
